import { Role } from './role.enum';

/**
 * Finance enums (T119, frozen — docs/architecture/14-finance.md, docs/requirements/14-finance.md).
 * Shared by database (T121), backend (T122–T124) and frontend (T125–T127). openapi.yaml v1.9.0
 * additive set.
 *
 * Pinned design invariants restated here as the binding wire contract:
 *  - EGP is the accounting base (DEC-FIN4, amends D7). USD enters ONLY as the source currency of the
 *    SAT/ACT exam-pricing rows and is converted to EGP at reservation time (never a stored balance).
 *  - Every money field in contracts/finance.ts is a DECIMAL-AS-STRING (e.g. "5725.00"), never a JS
 *    number — mirrors the calendar `amount` precedent and the T56 sub-cent lesson. $114.50 × rate
 *    must survive round-trip with no float drift.
 */

/**
 * Currencies now recognized by the system. DEC-FIN4 amends D7 ("EGP only"): USD is admitted STRICTLY
 * as the fixed source currency of the two USD exam sections (SAT, ACT). It never appears as a stored
 * balance, a payable/receivable, or a user-selectable currency anywhere outside exam pricing.
 * `CURRENCIES` in accounts.enum.ts stays `['EGP']` unchanged — tuition is still EGP-only.
 */
export const FINANCE_CURRENCIES = ['EGP', 'USD'] as const;
export type FinanceCurrency = (typeof FINANCE_CURRENCIES)[number];

/** The accounting base currency. Every revenue/ledger figure is normalized to this. */
export const ACCOUNTING_CURRENCY: FinanceCurrency = 'EGP';

/**
 * Fixed, non-editable currency per exam section (§3.1). The Admin edits cost/price amounts only —
 * never the currency, which is intrinsic to the exam board that issues the section. This is also the
 * guard that keeps USD entering ONLY via SAT/ACT (DEC-FIN4).
 */
export const EXAM_SECTION_CURRENCY = {
  SAT: 'USD',
  ACT: 'USD',
  'EST I': 'EGP',
  'EST II': 'EGP',
} as const;

/** True for the two sections whose reservation REQUIRES a usdEgpRate (SAT, ACT). */
export const USD_EXAM_SECTIONS = ['SAT', 'ACT'] as const;

/**
 * Other-expense categories (DEC-FIN8). Seed set — additive (new values may be appended later without
 * breaking stored rows); NOT exhaustive by user request.
 */
export const OTHER_EXPENSE_CATEGORIES = [
  'Rent',
  'Salaries',
  'Utilities',
  'Marketing',
  'Other',
] as const;
export type OtherExpenseCategory = (typeof OTHER_EXPENSE_CATEGORIES)[number];

/**
 * The kinds of ledger row (§8.2), i.e. the sources the derived UNION (DEC-FIN5) draws from.
 * `exam_reversal` is the contra entry emitted when a booked exam is cancelled (GAP-1, FINAL) — it is
 * NOT a delete; it is a later, separately-dated negative entry that nets the original to zero.
 *
 * T145/§19 appended `referral_cost` (DEC-REF6, docs/architecture/16-referral-partners.md §6) — the
 * 6th source query / 7th entry type. Unlike every other source, its underlying row is MUTABLE: a
 * partner reassignment recalculates the amount and a cleared link removes it, so a PAST period's
 * referral total can legitimately change between two runs of the same report (DEC-REF4, accepted by
 * the user — not a defect).
 */
export const LEDGER_ENTRY_TYPES = [
  'tuition_payment', // Income  (+) — installments.amount_paid
  'down_payment', //    Income  (+) — student_down_payments.amount (one-time per student)
  'exam_payment', //    Income  (+) — exam reservation snapshot priceAmountEgp
  'exam_cost', //       Expense (−) — exam reservation snapshot costAmountEgp
  'exam_reversal', //   Contra  (±) — reverses a cancelled booking's income + cost
  'school_cost', //     Expense (−) — one-time per-student school-cost recognition row
  'other_expense', //   Expense (−) — finance_other_expenses row
  'referral_cost', //   Expense (−) — per-student referral payout (student_referral_costs), MUTABLE
] as const;
export type LedgerEntryType = (typeof LEDGER_ENTRY_TYPES)[number];

/** Ledger row direction. `income` counts +, `expense` counts − toward the net total. */
export const LEDGER_DIRECTIONS = ['income', 'expense'] as const;
export type LedgerDirection = (typeof LEDGER_DIRECTIONS)[number];

/**
 * Lifecycle status of an exam-reservation finance snapshot (§4.3). The MONEY fields (amounts, rate,
 * dates) are immutable for life; only this lifecycle flag transitions:
 *  - `Active`   — the live snapshot; counts in revenue/ledger at reserveDate.
 *  - `Voided`   — a mis-entered snapshot corrected via void+re-reserve (§4.3 Q7); EXCLUDED entirely
 *                 (as if it never happened — never surfaced in revenue/ledger).
 *  - `Reversed` — a real reservation later cancelled (GAP-1); the original positive entries STILL
 *                 stand at reserveDate, and a separate contra row negates them at the cancel date.
 */
export const RESERVATION_SNAPSHOT_STATUSES = ['Active', 'Voided', 'Reversed'] as const;
export type ReservationSnapshotStatus = (typeof RESERVATION_SNAPSHOT_STATUSES)[number];

/**
 * Finance period selector — SAME value set and Cairo semantics as the dashboard's
 * DashboardSummaryPeriod (§14.7 / T98). Reuses the one and only cairo-date.util resolver server-side;
 * NEVER re-derives tz math. `custom` requires from+to (Cairo YYYY-MM-DD, from ≤ to, span ≤ 366 days).
 * Because every finance ledger date is stored as a Cairo calendar DATE (not a timestamp), the filter
 * needs only the resolved Cairo from/to date STRINGS and an inclusive DATE BETWEEN — no per-row UTC
 * boundary conversion (§10 of the design doc).
 */
export const FINANCE_PERIODS = ['today', 'this-month', 'this-year', 'custom'] as const;
export type FinancePeriod = (typeof FINANCE_PERIODS)[number];

/** Default period when the `/finance` page loads without an explicit selection. */
export const FINANCE_DEFAULT_PERIOD: FinancePeriod = 'this-month';

/**
 * Max inclusive span (days) for a `custom` finance range — mirrors the dashboard cap (D-DF4 / §14.7.3)
 * to bound query cost.
 */
export const FINANCE_MAX_CUSTOM_RANGE_DAYS = 366;

/**
 * DEC-FIN1 RBAC — the single source of truth for the two finance role tiers, shared by backend
 * (@Roles()) and frontend (nav-item + route-guard visibility). To change who owns finance, edit these
 * arrays only — no route/schema change. All 5 roles NOT listed here get 403 on every /finance/* route.
 */

/** Tier 1 — may WRITE money config (exam pricing + per-school cost). Admin only. */
export const FINANCE_CONFIG_WRITE_ROLES: Role[] = ['Admin'];

/**
 * Tier 2 — may READ money config + revenue/ledger, and CRUD other-expenses. Admin + Accounts Officer.
 * (Config READ is this same tier; config WRITE narrows to FINANCE_CONFIG_WRITE_ROLES.)
 */
export const FINANCE_READ_ROLES: Role[] = ['Admin', 'Accounts Officer'];

/** Roles that see the `/finance` nav item (UI convenience; the RolesGuard is the real boundary). */
export const FINANCE_NAV_ROLES: Role[] = FINANCE_READ_ROLES;
